/**
 * Allowlist de cookies do portal.
 *
 * Vários sistemas compartilham o mesmo domínio e o navegador envia todos
 * no header `Cookie`. Quando esse header passa de ~8–16 KB, nginx/Node
 * respondem 400 ("Request Header Or Cookie Too Large") e o portal quebra.
 *
 * A regra é manter só o que o portal precisa:
 * - autenticação Keycloak (`kcToken`, `kcIdToken`)
 * - modo sem cabeçalho em iframe (`xviaNoHeader`)
 * - preferências de acessibilidade (UserWay e VLibras)
 *
 * Qualquer outro cookie é removido do request e expirado no navegador.
 */
export const PORTAL_COOKIE_NAMES = ["kcToken", "kcIdToken", "xviaNoHeader"];

/** Prefixos do UserWay (`uw-*`, `userway*`) e do VLibras. */
export const ACCESSIBILITY_COOKIE_PREFIXES = [
  "uw-",
  "userway",
  "_userway",
  "vlibras",
  "vw-",
];

/**
 * Teto de cookies expirados por resposta.
 * Cada nome gera vários `Set-Cookie` (path/domínio); sem limite a resposta
 * também estouraria o tamanho do header.
 */
export const MAX_COOKIE_EXPIRE_PER_RESPONSE = 40;

export interface CookiePair {
  name: string;
  value: string;
}

export interface CookieClearTarget {
  name: string;
  path: string;
  domain?: string;
}

/** True se o cookie é de acessibilidade (UserWay ou VLibras). */
export function isAccessibilityCookieName(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  return ACCESSIBILITY_COOKIE_PREFIXES.some((prefix) =>
    normalized.startsWith(prefix)
  );
}

/** True se o cookie pode permanecer no header. */
export function isAllowedCookieName(name: string): boolean {
  const trimmed = name.trim();
  if (PORTAL_COOKIE_NAMES.includes(trimmed)) {
    return true;
  }
  return isAccessibilityCookieName(trimmed);
}

/** Quebra `Cookie: a=1; b=2` em pares nome/valor. */
export function parseCookieHeader(header: string): CookiePair[] {
  return header
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const separator = part.indexOf("=");
      if (separator === -1) {
        return { name: part, value: "" };
      }
      return {
        name: part.slice(0, separator).trim(),
        value: part.slice(separator + 1),
      };
    });
}

/**
 * Filtra o header `Cookie`: devolve só os permitidos e a lista dos
 * nomes que devem ser apagados no navegador.
 */
export function keepAllowedCookies(header: string | undefined): {
  header: string | undefined;
  removed: string[];
} {
  if (!header) {
    return { header: undefined, removed: [] };
  }

  const kept: string[] = [];
  const removed = new Set<string>();

  for (const pair of parseCookieHeader(header)) {
    if (isAllowedCookieName(pair.name)) {
      kept.push(`${pair.name}=${pair.value}`);
      continue;
    }
    removed.add(pair.name);
  }

  return {
    header: kept.length ? kept.join("; ") : undefined,
    removed: Array.from(removed),
  };
}

/**
 * Domínios em que o cookie pode ter sido gravado.
 * O navegador identifica o cookie por (nome + domain + path); para apagar
 * é preciso repetir o mesmo Domain original.
 *
 * Ex.: www.pidigital.pi.gov.br → .www..., .pidigital.pi.gov.br, .pi.gov.br
 * (pula .gov.br / .br, que são sufixos públicos).
 */
export function cookieDomainCandidates(hostname: string): string[] {
  const host = hostname.split(":")[0].toLowerCase();
  if (!host || host === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return [];
  }

  const labels = host.split(".").filter(Boolean);
  const candidates: string[] = [];

  for (let i = 0; i < labels.length - 1; i++) {
    const remaining = labels.slice(i);
    const tld = remaining[remaining.length - 1];
    const minLabels = tld === "br" ? 3 : 2;
    if (remaining.length < minLabels) {
      continue;
    }
    candidates.push(`.${remaining.join(".")}`);
  }

  return candidates;
}

/**
 * Paths em que o cookie pode ter sido gravado.
 * Sempre tenta `/` e o primeiro segmento (ex.: `/app`).
 */
export function cookiePathCandidates(pathname: string): string[] {
  const paths = new Set<string>(["/"]);
  const normalized = pathname.split("?")[0].split("#")[0];
  if (!normalized || normalized === "/") {
    return Array.from(paths);
  }

  const segments = normalized.split("/").filter(Boolean);
  if (segments[0]) {
    paths.add(`/${segments[0]}`);
  }

  return Array.from(paths);
}

/**
 * Combinações (path × domain) usadas para expirar cada cookie extra.
 * Inclui a variante host-only (sem Domain), que é como o portal grava
 * `kcToken`.
 */
export function cookieClearTargets(
  names: string[],
  hostname: string,
  pathname = "/",
  maxNames = MAX_COOKIE_EXPIRE_PER_RESPONSE
): CookieClearTarget[] {
  const paths = cookiePathCandidates(pathname);
  const domains = cookieDomainCandidates(hostname);
  const targets: CookieClearTarget[] = [];

  for (const name of names.slice(0, maxNames)) {
    for (const path of paths) {
      targets.push({ name, path });
      for (const domain of domains) {
        targets.push({ name, path, domain });
      }
    }
  }

  return targets;
}

const EXPIRED_COOKIE_DATE = "Thu, 01 Jan 1970 00:00:00 GMT";

/**
 * Limpeza no navegador: apaga cookies visíveis em `document.cookie`
 * que não estão na allowlist.
 *
 * Cookies HttpOnly só o servidor consegue apagar; esta função cobre o
 * restante (scripts de outros apps no mesmo domínio).
 */
export function pruneDisallowedBrowserCookies(
  cookieHeader = typeof document !== "undefined" ? document.cookie : "",
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  pathname = typeof window !== "undefined" ? window.location.pathname : "/"
): string[] {
  if (typeof document === "undefined") {
    return [];
  }

  const { removed } = keepAllowedCookies(cookieHeader);
  if (!removed.length) {
    return [];
  }

  const targets = cookieClearTargets(removed, hostname, pathname);
  for (const target of targets) {
    const domainAttr = target.domain ? `; domain=${target.domain}` : "";
    document.cookie = `${target.name}=; expires=${EXPIRED_COOKIE_DATE}; max-age=0; path=${target.path}${domainAttr}`;
  }

  return removed;
}
